/**
 * FileTypeIcon — SSOT for file-extension → icon mapping.
 *
 * Takes a filename string and renders the appropriate Icon for the file type.
 * Add new extensions here; do not duplicate this logic elsewhere.
 */
import React from 'react';
import { Icon, IconName } from '../atoms/Icon';

// ─── Extension → IconName map ────────────────────────────────────────────────

const EXT_MAP: Record<string, IconName> = {
  // Images
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image',
  webp: 'image', svg: 'image', heic: 'image', avif: 'image',

  // Video
  mp4: 'video', mov: 'video', avi: 'video', mkv: 'video',
  webm: 'video', wmv: 'video', m4v: 'video',

  // Audio
  mp3: 'music', m4a: 'music', wav: 'music', flac: 'music',
  aac: 'music', ogg: 'music',

  // PDF
  pdf: 'file-text',

  // Documents
  doc: 'file-text', docx: 'file-text', txt: 'file-text',
  md: 'file-text', rtf: 'file-text', odt: 'file-text',
  xls: 'file-text', xlsx: 'file-text', csv: 'file-text',
  ppt: 'file-text', pptx: 'file-text',

  // Archives
  zip: 'archive', tar: 'archive', gz: 'archive', rar: 'archive',
  '7z': 'archive', bz2: 'archive', xz: 'archive',
};

const DEFAULT_ICON: IconName = 'file';

// ─── Component ───────────────────────────────────────────────────────────────

export interface FileTypeIconProps {
  filename: string;
  size?: number;
  color: string;
}

export const FileTypeIcon: React.FC<FileTypeIconProps> = ({ filename, size = 14, color }) => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const iconName: IconName = EXT_MAP[ext] ?? DEFAULT_ICON;
  return <Icon name={iconName} size={size} color={color} />;
};
